import React, { useState } from 'react';
import { Box, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

/**
 * Component for displaying image attachments in wiki entries
 * @param {array} attachments - Array of all attachments
 */
const ImageAttachmentDisplay = ({ attachments = [] }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Filter only image attachments
  const imageAttachments = attachments.filter(att => 
    att.type && att.type.startsWith('image/')
  );

  if (imageAttachments.length === 0) {
    return null;
  }

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  const isSingleImage = imageAttachments.length === 1;

  return (
    <>
      {isSingleImage ? (
        // Single image - fit to aspect ratio with no whitespace
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              position: 'relative',
              maxWidth: '100%',
              maxHeight: '600px',
              borderRadius: 2,
              border: '2px solid #ddd',
              overflow: 'hidden',
              '&:hover': {
                border: '2px solid #9a7a4a',
                '& .zoom-overlay': {
                  opacity: 1
                }
              }
            }}
            onClick={() => handleImageClick(imageAttachments[0].url)}
          >
            <img
              src={imageAttachments[0].url}
              alt={imageAttachments[0].name}
              loading="lazy"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '600px',
                height: 'auto',
                width: 'auto'
              }}
            />
            <Box
              className="zoom-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.3s',
                pointerEvents: 'none'
              }}
            >
              <ZoomInIcon sx={{ color: 'white', fontSize: 48 }} />
            </Box>
          </Box>
        </Box>
      ) : (
        // Multiple images - use flex layout to preserve aspect ratios
        <Box 
          sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
            mb: 3,
            alignItems: 'flex-start',
            justifyContent: 'flex-start'
          }}
        >
          {imageAttachments.map((attachment, index) => (
            <Box
              key={index}
              sx={{
                cursor: 'pointer',
                position: 'relative',
                maxWidth: imageAttachments.length === 2 ? 'calc(50% - 8px)' : 'calc(33.333% - 11px)',
                maxHeight: '400px',
                borderRadius: 2,
                border: '2px solid #ddd',
                overflow: 'hidden',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  border: '2px solid #9a7a4a',
                  '& .zoom-overlay': {
                    opacity: 1
                  }
                }
              }}
              onClick={() => handleImageClick(attachment.url)}
            >
              <img
                src={attachment.url}
                alt={attachment.name}
                loading="lazy"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  width: 'auto',
                  height: 'auto',
                  display: 'block'
                }}
              />
              <Box
                className="zoom-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                  pointerEvents: 'none'
                }}
              >
                <ZoomInIcon sx={{ color: 'white', fontSize: 48 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Full-size image dialog */}
      <Dialog
        open={Boolean(selectedImage)}
        onClose={handleClose}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 2
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center'
          }
        }}
      >
        <Box sx={{ 
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)'
              },
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full size"
              style={{
                maxWidth: '95vw',
                maxHeight: '95vh',
                width: 'auto',
                height: 'auto',
                display: 'block',
                objectFit: 'contain'
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ImageAttachmentDisplay;
